import React, {useContext} from "react";
import {useAsync2} from "nate-react-api-helpers";
import {useNavigate} from "react-router-dom";
import {SelectEditor} from "../../../../misc/scroller/SelectEditor";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {api} from "../../../../api/API";
import {adoptHardwareElevation} from "../../shopdrawing/openings/adoptHardwareElevation";
import {useConfirm} from "../../../../misc/Confirm";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useSnackAction} from "../../shopdrawing/hardware/SnackAction";
import {DropdownDataLoading} from "../../settings/DropDowns";

// quote-mode hardware elevation picker — the quote-opening equivalent of the shop-drawing
// PickHardwareElevation. Lists the alternative's existing hardware elevations; selecting one
// clones the source into this quote opening's scope and links it. The free-solo "draw new"
// option unlinks the opening and opens it in the elevation tool.
export function PickHardwareElevation(props: {
    anchor: any;
    row: {id: number};
    width: number;
    alternative: number;
    onCancel(): void;
}) {
    const project = useProjectId();
    const editCtx = useContext(EditContext);
    const confirm = useConfirm();
    const navigate = useNavigate();

    const list = useAsync2((p: number) => api.elevationTool.listHardwareElevations({project: p, alternative: props.alternative}), project, [project, props.alternative]);

    const adopt = useSnackAction(async (hardware: number) => {
        await adoptHardwareElevation(confirm.confirm, {project, quoteOpening: props.row.id, hardware});
        await editCtx.reload(true, [props.row.id]);
        props.onCancel();
    }, {success: "Hardware elevation linked"});

    // free-solo: unlink this quote opening's hardware elevation, then open the tool, passing
    // the typed name so it auto-saves a fresh hardware elevation under it.
    const drawNew = useSnackAction(async (name: string) => {
        await api.elevationTool.unlinkHardware({project, quoteOpening: props.row.id});
        navigate(`/project/${project}/quote/${props.alternative}/elevation-tool?quoteOpening=${props.row.id}&alternative=${props.alternative}&workspace=hardware&autoSaveName=${encodeURIComponent(name.trim())}`);
    }, {success: "Unlinked — opening elevation tool"});

    if(list.loading) return <DropdownDataLoading anchor={props.anchor} width={props.width} />;

    const options = list.asList.map(s => ({value: s.id.toString(), display: s.name}));

    return (
        <SelectEditor
            anchor={props.anchor}
            options={options}
            value=""
            initialValue=""
            freeSolo
            onBlur={props.onCancel}
            onSelect={(value, freeSolo) => freeSolo ? drawNew.callback(value) : adopt.callback(parseInt(value))}
        />
    );
}
